import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { TextBulletList } from "../display/TextFormat";
import { SlideInAnim } from "../tools/Animations";
import { HealthSection } from "../health/HealthSection";
import { HealthSectionHeader } from "../health/HealthSectionHeader";
import { getLocalized, HealthLang, LocalizedString } from "../health/healthTypes";
import { healthCol, healthColStack, healthSplitLg } from "../health/healthLayout";
import { healthBody, healthSectionEyebrow } from "../health/healthVisualStyles";

const imagePath = "image/robotics/";

interface AgentsIntroSectionProps {
    data: DataKeys;
}

export function AgentsIntroSection({ data }: AgentsIntroSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const sectionId = (data?.id as string) || "intro";
    const title = getLocalized(data?.title, hLang);
    const body = getLocalized(data?.body, hLang);
    const bullets: string[] = (data?.bullets ?? []).map((b: DataKeys) =>
        getLocalized(b as LocalizedString, hLang)
    );
    const imageName = data?.image as string | undefined;
    const imageAlt = getLocalized(data?.imageAlt, hLang) || title;

    return (
        <HealthSection id={sectionId} ariaLabelledBy={`${sectionId}-title`} compact>
            <div className={`${healthSplitLg} lg:items-start`}>
                <div className={`${healthCol} flex flex-col gap-6`}>
                    <p className={healthSectionEyebrow}>
                        {getLocalized(data?.eyebrow, hLang)}
                    </p>
                    <HealthSectionHeader
                        title={title}
                        titleId={`${sectionId}-title`}
                        className="mb-0 lg:mb-0"
                    />
                    <div className={healthColStack}>
                        <p className={healthBody}>{body}</p>
                        {bullets.length > 0 && (
                            <TextBulletList
                                list={bullets}
                                textStyle="text-left text-base text-grey-500"
                            />
                        )}
                    </div>
                </div>
                {imageName && (
                    <div className={healthCol}>
                        <SlideInAnim direction="right">
                            <div className="w-full overflow-hidden rounded-2xl">
                                <div className="aspect-[4/3] w-full lg:max-h-[420px]">
                                    <img
                                        src={require(`../../${imagePath}${imageName}`)}
                                        alt={imageAlt}
                                        className="h-full w-full rounded-2xl object-cover"
                                        loading="lazy"
                                    />
                                </div>
                            </div>
                        </SlideInAnim>
                    </div>
                )}
            </div>
        </HealthSection>
    );
}
